import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { MCard, MSpinner } from "components/Shared";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_PDF_SETTINGS_EDIT } from "typeConstants/rights";
import {
  DATA_TYPE_DATE,
  DATA_TYPE_BOOLEAN,
} from "typeConstants/dataTypes";
import { getPdfExportSettings, setPdfExportSettings, getPdfExportSettingsFields } from "requests/customer";
import * as yup from "yup";
import { PDFExportSettingsDto, PDFExportSettingsFields } from "interfaces/Mueller.Business.Models";
import responseHandler from "responseHandler";

interface PDFExportSettingsProps {
  customerNumber: number,
  cellWidth: number,
  userHasRight: boolean,
  makeToast: (text: string, type: string) => void,
}

const PDFExportSettings = ({
  customerNumber,
  cellWidth,
  userHasRight,  
  makeToast,
}: PDFExportSettingsProps) => {
  const [exportSettings, setExportSettings] = useState<PDFExportSettingsDto>(null);
  const [exportSettingsFields, setExportSettingsFields] = useState<PDFExportSettingsFields>(null);
  const mounted = useRef(true);
  const cardControlMethods = useRef();

  useEffect(() => {
    if(customerNumber){
      getPdfExportSettings(customerNumber, (resp) => {
        setExportSettings(resp);
      }, mounted);
    }
    getPdfExportSettingsFields((resp) => {
      setExportSettingsFields(resp);
    }, mounted);    
    return () => mounted.current = false;
  }, []);

  const cardEditFinished = (fieldsChanged) => {
    if (fieldsChanged.length) {
      cardControlMethods.current.refreshEditData();
      if(!cardControlMethods.current.runValidation(true)){
        makeToast("Please check form for validation errors", "error");       
      }
      else {
        let updated = { ...exportSettings, ...cardControlMethods.current.getEditedDataObject(true) };
        updated.customerNumber = customerNumber;
        setPdfExportSettings(customerNumber, updated, (resp) => {
          makeToast("PDF Export Settings Saved", "success");
          getPdfExportSettings(customerNumber, (resp) => {
            setExportSettings(resp);
          }, mounted);           
        }, mounted);
      }     
    }
  };

  let fields = [];

  const makeYupValidation = (requiredMessage = null, regex = null, regexFailedMessage = null) => {
    let yerp = yup.string().nullable();
    
    if (requiredMessage) yerp = yerp.required(requiredMessage);
    if (regex) yerp = yerp.matches(regex, { excludeEmptyString: true, message: regexFailedMessage });

    return yerp;
  };

  if (exportSettings && exportSettingsFields) {
    fields = [
      {
        key: "startDate",
        label: "Start Date",
        value: exportSettings.startDate,
        editable: userHasRight,
        validation: makeYupValidation("Start Date is Required"),
        dataType: DATA_TYPE_DATE
      },
      {
        key: "inactiveDate",
        label: "Inactive Date",
        value: exportSettings.inactiveDate,
        editable: userHasRight,
        dataType: DATA_TYPE_DATE
      },
      {
        key: "customClass",
        label: "Custom Class",
        value: exportSettings.customClass,
        editable: userHasRight,
        validation: makeYupValidation(null, /[A-Za-z]/, "Custom Class is invalid")
      },
      {
        key: "fileTemplateName",
        label: "Template Name",
        value: exportSettings.fileTemplateName,
        editable: userHasRight,
        tooltip: {
          title: "Fields",
          isEditable: false ,         
          fields: exportSettingsFields.fields.map(f => ({
            key: f,
            label: "",
            value: f,
            editable: false,            
           }))
        },
        validation: makeYupValidation("Template Name is required", /[A-Za-z]/, "Template Name is invalid")
      }, 
      userHasRight && {
        key: "templateExample",
        label: "",
        value: "Example: {PolicyNumber}-{AgencyCode}.PDF",
        editable: false,
        className: "card-example-field"
      },     
      {
        key: "localFolderEnabled",
        label: "Local Folder Enabled",
        value: exportSettings.localFolderEnabled,
        editable: userHasRight,
        dataType: DATA_TYPE_BOOLEAN,
      },
      {
        key: "destinationPath",
        label: "Local Destination Path",
        value: exportSettings.destinationPath,
        editable: userHasRight,
        validation: exportSettings.localFolderEnabled ? makeYupValidation("Local Destination Path is required", /[A-Za-z]/, "Local Destination Path is invalid") : null,
      },
      {
        key: "pgpEnabled",
        label: "PGP Enabled",
        value: exportSettings.pgpEnabled,
        editable: userHasRight,
        dataType: DATA_TYPE_BOOLEAN,        
      },
      {
        key: "pgpEncryptSender",
        label: "PGP Encrypt Sender",
        value: exportSettings.pgpEncryptSender,
        editable: userHasRight,
        validation: exportSettings.pgpEnabled ? makeYupValidation("PGP Encrypt Sender is required", /[A-Za-z]/, "PGP Encrypt Sender is invalid") : null,
      },   
      {
        key: "pgpEncryptSenderPassword",
        label: "PGP Encrypt Sender Password",
        value: exportSettings.pgpEncryptSenderPassword,
        editable: userHasRight,
        validation: exportSettings.pgpEnabled ? makeYupValidation("PGP Encrypt Sender Password is required", /[A-Za-z]/, "PGP Encrypt Sender Password is invalid") : null,
      },  
      exportSettings.pgpEnabled && {
        key: "pgpEncryptRecipient",
        label: "PGP Encrypt Recipient",
        value: exportSettings.pgpEncryptRecipient,
        editable: userHasRight,
        validation: exportSettings.pgpEnabled ? makeYupValidation("PGP Encrypt Recipient is required", /[A-Za-z]/, "PGP Encrypt Recipient is invalid") : null,
      }, 
      {
        key: "ftpEnabled",
        label: "FTP Enabled",
        value: exportSettings.ftpEnabled,
        editable: userHasRight,
        dataType: DATA_TYPE_BOOLEAN,            
      },
      {
        key: "ftpUploadToServer",
        label: "Server",
        value: exportSettings.ftpUploadToServer,
        editable: userHasRight,
        validation: makeYupValidation(null, /[A-Za-z]/, "FTP Server is invalid"),
      }, 
      {
        key: "ftpUploadToPath",
        label: "Path",
        value: exportSettings.ftpUploadToPath,
        editable: userHasRight,
        validation: makeYupValidation(null, /[A-Za-z]/, "FTP Path is invalid"),        
      },
      {
        key: "ftpUploadToUsername",
        label: "Username",
        value: exportSettings.ftpUploadToUsername,
        editable: userHasRight,
        validation: exportSettings.ftpUploadToServer ? makeYupValidation("FTP Username is required", /[A-Za-z]/, "FTP Username is invalid") : null,
      },  
      {
        key: "ftpUploadToPassword",
        label: "Password",
        value: exportSettings.ftpUploadToPassword,
        editable: userHasRight,
        validation: exportSettings.ftpUploadToServer ? makeYupValidation("FTP Username is required", /[A-Za-z]/, "FTP Password is invalid") : null,
      }, 
      {
        key: "ftpUploadToUsePassive",
        label: "Passive Mode",
        value: exportSettings.ftpUploadToUsePassive,
        editable: userHasRight,
        dataType: DATA_TYPE_BOOLEAN,
      }, 
      {
        key: "ftpEnableSSL",
        label: "SSL Enabled",
        value: exportSettings.ftpEnableSSL,
        editable: userHasRight,
        dataType: DATA_TYPE_BOOLEAN,
      }
    ];  
  }

  if (!exportSettings) {
    return <MSpinner />;
  } else {
    return (<MCard
      isFetching={!exportSettings}
      title="PDF Export Settings"
      cellWidth={cellWidth}
      fields={fields}
      onEditFinished={cardEditFinished}
      isEditable={userHasRight}
      getControlMethods={cm => cardControlMethods.current = cm}
    />
  )};
};

const mapStateToProps = state => ({
  customerDetails: state.customer.customerDetails,
  userHasRight: userHasRight(CUSTOMER_PDF_SETTINGS_EDIT)(state),
});

const mapDispatchToProps = dispatch => ({
  makeToast: (text, type) => {
    responseHandler.customToast(text, type, dispatch);
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(PDFExportSettings);
